// Library Imports
import { useMemo } from 'react';
import { matchPath, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Local Imports
import { ROUTE_PATHS } from '@/router/constants';
import { getRoleHomePath } from '@/router/roles';
import { useRoutePermissions } from '@/router/hooks/useRoutePermissions';
import { creatorRoutes } from '@/router/routes/creatorRoutes';
import { adminRoutes } from '@/router/routes/adminRoutes';
import { brandRoutes } from '@/router/routes/brandRoutes';

const ROUTES_BY_ROLE = {
  creator: creatorRoutes,
  admin: adminRoutes,
  brand: brandRoutes,
};

const flattenRoutes = (routes) => {
  const flat = [];
  routes?.forEach(route => {
    flat.push(route);
    route?.child?.forEach(child => flat.push(child));
  });
  return flat;
};

const isPathAllowedForRole = (pathname, role, canAccessRoute) => {
  const routes = flattenRoutes(ROUTES_BY_ROLE[role] ?? []);
  return routes.some(route => {
    if (!route?.path || route.path === '#') return false;
    if (!canAccessRoute(route.moduleName)) return false;
    return matchPath({ path: route.path, end: true }, pathname) != null;
  });
};

export const RoleRouteGuard = () => {
  const location = useLocation();
  const currentUser = useSelector(state => state?.currentUser?.currentUser);
  const { userRole, canAccessRoute } = useRoutePermissions();

  const isAllowed = useMemo(
    () => userRole && isPathAllowedForRole(location.pathname, userRole, canAccessRoute),
    [location.pathname, userRole, canAccessRoute]
  );

  if (!userRole) {
    return <Navigate to={ROUTE_PATHS.LOGIN} replace />;
  }

  if (!isAllowed) {
    return <Navigate to={getRoleHomePath(currentUser)} replace />;
  }

  return <Outlet />;
};
